import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useGatewayStatus } from '../hooks/useClaw9';
import { getGatewayLogs } from '../api/gateway';
import AgentManagerPanel from './AgentManagerPanel';
import ChannelBindingsPanel from './ChannelBindingsPanel';

/**
 * Panel displaying gateway status with controls
 */
function GatewayStatusPanel({ className = '' }) {
    const {
        status,
        config,
        isLoading,
        error,
        refresh,
        restartGateway,
        reloadConfig
    } = useGatewayStatus(30000); // Poll every 30 seconds

    const [activeSection, setActiveSection] = useState('status');
    const [isRestarting, setIsRestarting] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [actionError, setActionError] = useState(null);

    const handleRestart = async () => {
        if (!window.confirm('Are you sure you want to restart the gateway? This may cause temporary service interruption.')) {
            return;
        }

        setIsRestarting(true);
        setActionError(null);
        try {
            await restartGateway();
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsRestarting(false);
        }
    };

    const handleReload = async () => {
        setIsReloading(true);
        setActionError(null);
        try {
            await reloadConfig();
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsReloading(false);
        }
    };

    const fetchLogs = async () => {
        setLoadingLogs(true);
        try {
            const result = await getGatewayLogs({ lines: 100 });
            setLogs(result.data || []);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setLoadingLogs(false);
        }
    };

    const getStatusIndicator = () => {
        const statusValue = status?.status || status?.state;
        switch (statusValue?.toLowerCase()) {
            case 'running':
            case 'healthy':
            case 'active':
                return <span className="h-3 w-3 rounded-full bg-green-500" />;
            case 'starting':
            case 'restarting':
                return <span className="h-3 w-3 rounded-full bg-yellow-500 animate-pulse" />;
            case 'stopped':
            case 'error':
            case 'unhealthy':
                return <span className="h-3 w-3 rounded-full bg-red-500" />;
            default:
                return <span className="h-3 w-3 rounded-full bg-gray-400" />;
        }
    };

    const sections = [
        { id: 'status', label: 'Status' },
        { id: 'agents', label: 'Agents' },
        { id: 'bindings', label: 'Bindings' },
        { id: 'logs', label: 'Logs' }
    ];

    if (isLoading && !status) {
        return (
            <div className={`flex items-center justify-center h-64 ${className}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-medium text-gray-900">Gateway</h2>
                    {getStatusIndicator()}
                    <span className="text-sm text-gray-500">
                        {status?.status || status?.state || 'Unknown'}
                    </span>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={refresh}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={handleReload}
                        disabled={isReloading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        {isReloading ? 'Reloading...' : 'Reload Config'}
                    </button>
                    <button
                        onClick={handleRestart}
                        disabled={isRestarting}
                        className="px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50"
                    >
                        {isRestarting ? 'Restarting...' : 'Restart Gateway'}
                    </button>
                </div>
            </div>

            {/* Errors */}
            {(error || actionError) && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error || actionError}</p>
                </div>
            )}

            {/* Section Tabs */}
            <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-8">
                    {sections.map(section => (
                        <button
                            key={section.id}
                            onClick={() => {
                                setActiveSection(section.id);
                                if (section.id === 'logs') fetchLogs();
                            }}
                            className={`
                                py-3 px-1 border-b-2 font-medium text-sm
                                ${activeSection === section.id
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }
                            `}
                        >
                            {section.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            {activeSection === 'status' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Status</h4>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {status?.status || status?.state || 'Unknown'}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Uptime</h4>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {status?.uptime || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Active Agents</h4>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {status?.activeAgents ?? config?.agents?.length ?? 0}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Channels</h4>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {status?.activeChannels ?? config?.channels?.length ?? 0}
                            </p>
                        </div>
                    </div>

                    {config && (
                        <div className="mt-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Configuration</h4>
                            <pre className="p-4 bg-gray-50 rounded-md text-sm text-gray-800 overflow-x-auto max-h-48">
                                {JSON.stringify(config, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {activeSection === 'agents' && (
                <AgentManagerPanel />
            )}

            {activeSection === 'bindings' && (
                <ChannelBindingsPanel />
            )}

            {activeSection === 'logs' && (
                <div className="bg-white rounded-lg border border-gray-200">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h4 className="text-sm font-medium text-gray-700">Gateway Logs</h4>
                        <button
                            onClick={fetchLogs}
                            disabled={loadingLogs}
                            className="px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                        >
                            {loadingLogs ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>
                    <div className="p-4 bg-gray-900 rounded-b-lg max-h-96 overflow-y-auto font-mono text-xs">
                        {loadingLogs ? (
                            <div className="text-gray-400">Loading logs...</div>
                        ) : logs.length === 0 ? (
                            <div className="text-gray-400">No logs available</div>
                        ) : (
                            logs.map((log, index) => (
                                <div key={index} className={`py-1 ${
                                    log.level === 'error' ? 'text-red-400' :
                                    log.level === 'warn' ? 'text-yellow-400' :
                                    'text-green-400'
                                }`}>
                                    <span className="text-gray-500">{log.timestamp} </span>
                                    <span className="uppercase">[{log.level}] </span>
                                    {log.message}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

GatewayStatusPanel.propTypes = {
    className: PropTypes.string
};

export default GatewayStatusPanel;
